"use client";

import { useCartStore, CartItem } from "@/lib/store/cartStore";
import { useQuery, useConvex, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
  X,
  ShoppingBag,
  Tag,
  Loader2,
  Trash2,
  MessageCircle,
} from "lucide-react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { trackViewCart } from "@/lib/analytics/gtag";
import { toast } from "@/lib/toast";
import { Id } from "@/convex/_generated/dataModel";
import { CartItemCard } from "./CartItemCard";
import { useSession } from "@/lib/auth/auth-client";
import { WhatsAppCheckoutDialog } from "./WhatsAppCheckoutDialog";
import { useBagTerms, useLinkedShelf } from "@/lib/shop/useBagTerms";
import { bagSummary, FREE_DELIVERY_RULE } from "@/lib/config/websiteAddOns";

export default function CartDrawer() {
  const {
    isOpen,
    closeCart,
    appliedPromo,
    setAppliedPromo,
    removePromo,
    sessionId,
    whatsappCheckoutOpen,
    openWhatsAppCheckout,
    closeWhatsAppCheckout,
  } = useCartStore();

  // Use useMutation hooks (authenticated WebSocket connection, not unauthenticated HTTP client)
  // @ts-ignore
  const removeCartItemMutation = useMutation(api.cart.core.removeCartItem);
  // @ts-ignore
  const clearCartMutation = useMutation(api.cart.core.clearCart);
  // @ts-ignore
  const updateQuantityMutation = useMutation(api.cart.core.updateCartItemQuantity);

  // Use the reactive useSession hook — no async delay, no null flash
  const { data: session, isPending: authPending } = useSession();
  const isAuthenticated = !authPending && !!session?.user;
  const authResolved = !authPending;

  // Subscribe to cart items from Convex
  // Skip until auth state is known, then use userId (authenticated) or sessionId (guest)
  const cartItems: CartItem[] | undefined = useQuery(
    api.cart.core.getCartItems as any,
    !authResolved
      ? "skip" // Wait for auth resolution
      : isAuthenticated
      ? {} // Authenticated - backend uses userId automatically
      : { sessionId: sessionId || undefined } // Guest - pass sessionId
  );

  // Convex client for one-shot promo validation
  const convex = useConvex();

  // Debug: Log cart query state
  useEffect(() => {
    console.log("[CartDrawer] Query state:", {
      sessionId,
      cartItems: cartItems === undefined ? "loading" : cartItems,
      itemCount: cartItems?.length || 0,
    });
  }, [sessionId, cartItems]);

  const [promoCode, setPromoCode] = useState("");
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [stockData, setStockData] = useState<Record<string, number>>({});
  const [isLoadingStock, setIsLoadingStock] = useState(false);

  // Loading state
  const isLoading = cartItems === undefined;
  const items = cartItems || [];

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  // The bag's money, the way the checkout and the order count it: each
  // item's price (`salePrice` is the OLD price before a sale, never what is
  // charged) plus the extras ticked, priced as the product stands today —
  // and whether delivery is free (lib/config/websiteAddOns.ts).
  const bagTerms = useBagTerms(items);
  const linkedShelf = useLinkedShelf(bagTerms);
  const summary = bagSummary(items, bagTerms, { method: "delivery", fee: 0 }, linkedShelf);
  const subtotal = summary.subtotal;

  const discountAmount = appliedPromo?.discount || 0;
  const totalPrice = Math.max(0, subtotal - discountAmount);

  // Fetch stock data when cart opens
  useEffect(() => {
    if (isOpen && items.length > 0 && !isLoading) {
      // Track cart view
      trackViewCart(
        items.map((item) => ({
          id: item.productId,
          name: item.productName,
          price: item.price,
          quantity: item.quantity,
        })),
        totalPrice
      );

      // Fetch stock data for all items
      const fetchStockData = async () => {
        setIsLoadingStock(true);
        try {
          const productIds = items.map((item) => item.productId);
          const response = await fetch("/api/inventory/check-stock-batch", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ productIds }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.stockMap) {
              const stockMap: Record<string, number> = {};
              Object.keys(data.stockMap).forEach((productId) => {
                stockMap[productId] = data.stockMap[productId].availableStock;
              });
              setStockData(stockMap);
            }
          }
        } catch (error) {
          console.error("Failed to fetch stock data:", error);
        } finally {
          setIsLoadingStock(false);
        }
      };

      fetchStockData();
    }
  }, [isOpen, items, totalPrice, isLoading]);

  // Handle quantity update with stock validation
  const handleQuantityUpdate = async (cartItemId: Id<"cartItems">, newQuantity: number) => {
    if (newQuantity < 1) {
      try {
        await removeCartItemMutation({ cartItemId, sessionId: sessionId || undefined });
        toast.success("Item removed from cart");
      } catch (error) {
        console.error("Failed to remove item:", error);
        toast.error("Failed to remove item");
      }
      return;
    }

    // Check stock limit before allowing increase
    const item = items.find((i) => i._id === cartItemId);
    if (!item) return;

    const availableStock = stockData[item.productId] ?? 999;
    if (newQuantity > availableStock) {
      toast.error(
        availableStock > 0
          ? `Only ${availableStock} items available in stock`
          : "This item is currently out of stock"
      );
      return;
    }

    try {
      await updateQuantityMutation({ cartItemId, quantity: newQuantity, sessionId: sessionId || undefined });
      toast.success("Cart updated");
    } catch (error) {
      console.error("Failed to update quantity:", error);
      toast.error("Failed to update cart");
    }
  };

  // Handle remove item — uses useMutation hook (authenticated WebSocket, not HTTP client)
  const handleRemoveItem = async (cartItemId: Id<"cartItems">) => {
    try {
      await removeCartItemMutation({
        cartItemId,
        sessionId: sessionId || undefined,
      });
      toast.success("Item removed from cart");
    } catch (error: any) {
      console.error("Failed to remove item:", error);
      toast.error("Failed to remove item from cart");
    }
  };

  // Handle clear entire cart — uses useMutation hook (authenticated WebSocket)
  const handleClearCart = async () => {
    const brandId = items[0]?.brandId as Id<"brands"> | undefined;
    if (!brandId) return;
    try {
      const result = await clearCartMutation({
        brandId,
        sessionId: sessionId || undefined,
      });
      if (result.deletedCount === 0) {
        toast.info("Cart is already empty");
      } else {
        toast.success(`Cleared ${result.deletedCount} item${result.deletedCount !== 1 ? "s" : ""} from cart`);
      }
    } catch (error: any) {
      console.error("Failed to clear cart:", error);
      toast.error("Failed to clear cart");
    }
  };

  // Apply promo code (direct Convex query — no API route)
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    // Derive brandId from cart items
    const brandId = items[0]?.brandId as Id<"brands"> | undefined;
    if (!brandId) {
      toast.error("Your cart is empty");
      return;
    }

    setIsValidatingPromo(true);
    try {
      const result = await convex.query(api.orders.promotions.validatePromoCode, {
        brandId,
        code: promoCode.toUpperCase().trim(),
        subtotal,
        channel: "online",
      });

      if (!result.valid) {
        toast.error(result.error || "Invalid promo code");
        return;
      }

      const promoData = result.promotion || result.promoCode;
      if (promoData) {
        setAppliedPromo({
          code: promoData.code,
          discount: promoData.discountAmount,
          discountValue: promoData.discountValue,
          type: promoData.discountType === "percentage" ? "percentage" : "fixed",
          promotionId: result.promotion?.id,
          isPersonalized: result.promotion?.isPersonalized,
        });
        toast.success(`Promo code applied! You saved ${promoData.discountAmount.toLocaleString()} UGX`);
        setPromoCode("");
      }
    } catch (error) {
      console.error("Error validating promo code:", error);
      toast.error("Failed to validate promo code");
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Remove promo code
  const handleRemovePromo = () => {
    removePromo();
    setPromoCode("");
    toast.success("Promo code removed");
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-2 top-2 bottom-2 w-[calc(100%-16px)] sm:w-[450px] bg-white shadow-2xl z-[70] flex flex-col rounded-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-6 h-6 text-black" />
                <h2 className="text-2xl font-bold text-black">Shopping Cart</h2>
              </div>
              <div className="flex items-center gap-2">
                {/* Clear all button — only when cart has items */}
                {items.length > 0 && (
                  <button
                    onClick={handleClearCart}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
                    title="Clear entire cart"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Clear all
                  </button>
                )}
                <button
                  onClick={closeCart}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Loader2 className="w-16 h-16 text-gray-300 mb-4 animate-spin" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Loading cart...
                  </h3>
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ShoppingBag className="w-16 h-16 text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Your cart is empty
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Add some products to get started
                  </p>
                  <Link
                    href="/shop"
                    onClick={closeCart}
                    className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                  >
                    Continue Shopping
                  </Link>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {items.map((item, index) => {
                    const availableStock = stockData[item.productId] ?? 999;

                    return (
                      <CartItemCard
                        key={item._id}
                        item={item}
                        availableStock={availableStock}
                        isLoadingStock={isLoadingStock}
                        onQuantityUpdate={handleQuantityUpdate}
                        onRemove={handleRemoveItem}
                        addOns={summary.lines[index]}
                      />
                    );
                  })}

                  {/* Continue Shopping — lives at the END of the scrollable
                      items list (not the footer) so it never eats into the
                      vertical space available for cart items. Closing the
                      drawer keeps the customer on the page they were on. */}
                  <button
                    onClick={closeCart}
                    className="w-full mt-1 py-2.5 text-center rounded-lg font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 touch-manipulation"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    Continue Shopping
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-gray-200 p-4 space-y-3">
                {/* Promo Code Section */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Have a promo code?
                  </label>
                  {appliedPromo ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          {appliedPromo.code}
                        </span>
                        <span className="text-xs text-green-600">
                          (
                          {appliedPromo.type === "percentage"
                            ? `${appliedPromo.discountValue}% off`
                            : `${appliedPromo.discountValue.toLocaleString()} UGX off`}
                          )
                        </span>
                      </div>
                      <button
                        onClick={handleRemovePromo}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) =>
                          setPromoCode(e.target.value.toUpperCase())
                        }
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleApplyPromo()
                        }
                        placeholder="Enter code"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      />
                      <button
                        onClick={handleApplyPromo}
                        disabled={isValidatingPromo || !promoCode.trim()}
                        className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isValidatingPromo ? "..." : "Apply"}
                      </button>
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-1 pt-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-black">
                      {Math.floor(subtotal).toLocaleString()} UGX
                    </span>
                  </div>

                  {appliedPromo && discountAmount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-600">Discount</span>
                      <span className="font-medium text-green-600">
                        -{Math.floor(discountAmount).toLocaleString()} UGX
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-base pt-1.5 border-t">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-black">
                      {Math.floor(totalPrice).toLocaleString()} UGX
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                  <span className="shrink-0">
                    {totalItems} item{totalItems !== 1 ? "s" : ""}
                  </span>
                  {summary.freeDelivery === "all" ? (
                    <span className="font-semibold text-emerald-700">Free delivery</span>
                  ) : summary.freeDelivery === "some" ? (
                    <span className="text-right">{FREE_DELIVERY_RULE}</span>
                  ) : (
                    <span>Delivery fee calculated at checkout</span>
                  )}
                </div>

                {/* Checkout Button */}
                <Link
                  href="/checkout"
                  onClick={closeCart}
                  className="w-full py-2.5 bg-black text-white text-center rounded-lg font-semibold hover:bg-gray-800 transition-colors touch-manipulation min-h-[40px] flex items-center justify-center"
                >
                  Proceed to Checkout
                </Link>

                {/* Order on WhatsApp — preferred path for customers who
                    aren't comfortable filling a full web checkout. Opens
                    the WhatsAppCheckoutDialog where they enter name +
                    phone and we route the order through our online-orders
                    pipeline + send a WhatsApp alert to the brand. */}
                <button
                  onClick={openWhatsAppCheckout}
                  className="w-full py-2.5 bg-[#25D366] text-white text-center rounded-lg font-semibold hover:bg-[#1ebe57] transition-colors flex items-center justify-center gap-2 touch-manipulation min-h-[40px]"
                >
                  <MessageCircle className="w-4 h-4 fill-white" />
                  Order on WhatsApp
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>

    {/* WhatsApp Checkout Dialog — rendered OUTSIDE the cart's
        AnimatePresence so it stays mounted independently of the cart's
        open/close state. If it lived inside, the cart's exit transition
        would unmount it during the same frame the user clicks the
        button (since openWhatsAppCheckout sets isOpen=false), causing a
        ~300ms gap before it appears. */}
    <WhatsAppCheckoutDialog
      isOpen={whatsappCheckoutOpen}
      onClose={closeWhatsAppCheckout}
      cartItems={items}
      lines={summary.lines}
      subtotal={subtotal}
      appliedPromo={appliedPromo}
      totalPrice={totalPrice}
      brandId={items[0]?.brandId}
      brandName={items[0]?.brandName}
      onSuccess={async () => {
        try {
          await handleClearCart();
        } catch {
          /* non-fatal — server side cleanup will handle stale carts */
        }
        removePromo();
        closeWhatsAppCheckout();
        closeCart();
      }}
    />
    </>
  );
}
